import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, ArrowLeft, Plus, X, Upload } from 'lucide-react';
import api from '../utils/api';

interface ProductFormData {
  name: string;
  description: string;
  price: number;
  category: string;
  images: string[];
  colors: string[];
  features: string[];
  specifications: Record<string, any>;
}

interface HsvColor {
  h: number;
  s: number;
  v: number;
}

const clamp = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value));

const hsvToHex = (h: number, s: number, v: number): string => {
  const normalizedH = ((h % 360) + 360) % 360;
  const normalizedS = clamp(s, 0, 100) / 100;
  const normalizedV = clamp(v, 0, 100) / 100;
  const c = normalizedV * normalizedS;
  const x = c * (1 - Math.abs(((normalizedH / 60) % 2) - 1));
  const m = normalizedV - c;

  let rPrime = 0;
  let gPrime = 0;
  let bPrime = 0;

  if (normalizedH < 60) {
    rPrime = c;
    gPrime = x;
  } else if (normalizedH < 120) {
    rPrime = x;
    gPrime = c;
  } else if (normalizedH < 180) {
    gPrime = c;
    bPrime = x;
  } else if (normalizedH < 240) {
    gPrime = x;
    bPrime = c;
  } else if (normalizedH < 300) {
    rPrime = x;
    bPrime = c;
  } else {
    rPrime = c;
    bPrime = x;
  }

  const r = Math.round((rPrime + m) * 255);
  const g = Math.round((gPrime + m) * 255);
  const b = Math.round((bPrime + m) * 255);

  return `#${[r, g, b].map((channel) => channel.toString(16).padStart(2, '0')).join('').toUpperCase()}`;
};

const hexToHsv = (hex: string): HsvColor => {
  const cleaned = hex.replace('#', '');
  if (!/^[0-9A-Fa-f]{6}$/.test(cleaned)) {
    return { h: 0, s: 0, v: 0 };
  }

  const r = parseInt(cleaned.slice(0, 2), 16) / 255;
  const g = parseInt(cleaned.slice(2, 4), 16) / 255;
  const b = parseInt(cleaned.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;

  let h = 0;
  if (delta !== 0) {
    if (max === r) h = 60 * (((g - b) / delta) % 6);
    else if (max === g) h = 60 * ((b - r) / delta + 2);
    else h = 60 * ((r - g) / delta + 4);
  }
  if (h < 0) h += 360;

  const s = max === 0 ? 0 : (delta / max) * 100;
  const v = max * 100;

  return { h: Math.round(h), s: Math.round(s), v: Math.round(v) };
};

const initialHsv = hexToHsv('#D946EF');

const AdminProductForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = Boolean(id);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [formData, setFormData] = useState<ProductFormData>({
    name: '',
    description: '',
    price: 0,
    category: '',
    images: [],
    colors: [],
    features: [],
    specifications: {}
  });
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState<string[]>([]);
  const [newFeature, setNewFeature] = useState('');
  const [newSpecKey, setNewSpecKey] = useState('');
  const [newSpecValue, setNewSpecValue] = useState('');
  const [newColor, setNewColor] = useState('#D946EF');
  const [hue, setHue] = useState(initialHsv.h);
  const [saturation, setSaturation] = useState(initialHsv.s);
  const [brightness, setBrightness] = useState(initialHsv.v);
  const [uploadingImages, setUploadingImages] = useState(false);

  useEffect(() => {
    fetchCategories();
    if (isEditing) {
      fetchProduct();
    }
  }, [id]);

  // Debug: Log whenever images change
  useEffect(() => {
    console.log('formData.images changed:', formData.images);
  }, [formData.images]);

  useEffect(() => {
    setNewColor(hsvToHex(hue, saturation, brightness));
  }, [hue, saturation, brightness]);

  const fetchCategories = async () => {
    try {
      const response = await api.get('/api/products/categories/list');
      const apiCategories = response.data;
      
      // Add predefined Arabic categories if they don't exist
      const predefinedCategories = ['شموع', 'توزيعات', 'هاندميد كروشيه', 'اخرى'];
      const allCategories = Array.from(new Set([...predefinedCategories, ...apiCategories]));
      
      setCategories(allCategories);
    } catch (error) {
      console.error('Error fetching categories:', error);
      // Fallback to predefined categories if API fails
      setCategories(['شموع', 'توزيعات', 'هاندميد كروشيه', 'اخرى']);
    }
  };

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/products/${id}`);
      const product = response.data;
      setFormData({
        name: product.name,
        description: product.description,
        price: product.price,
        category: product.category,
        images: product.images || [],
        colors: product.colors || [],
        features: product.features || [],
        specifications: product.specifications || {}
      });
    } catch (error) {
      console.error('Error fetching product:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (files: FileList) => {
    if (files.length === 0) return;

    setUploadingImages(true);
    try {
      console.log('Files to upload:', files);
      const uploadedUrls: string[] = [];
      
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        console.log('Uploading file:', file.name, file.type, file.size);
        
        const formData = new FormData();
        formData.append('image', file);
        
        console.log('FormData created:', formData);
        
        const response = await api.post('/api/products/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        
        console.log('Upload response:', response.data);
        uploadedUrls.push(response.data.imageUrl);
      }
      
      console.log('All uploaded URLs:', uploadedUrls);
      console.log('Current formData.images before update:', formData.images);
      
      setFormData(prev => {
        const newImages = [...prev.images, ...uploadedUrls];
        console.log('Setting new images array:', newImages);
        return {
          ...prev,
          images: newImages
        };
      });
      
      // Note: formData.images won't be updated here yet due to React's async state updates
      // The actual update will be visible in the useEffect hook
      
    } catch (error: any) {
      console.error('Error uploading images:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('Error headers:', error.response?.headers);
      
      let errorMessage = 'Error uploading images. Please try again.';
      
      if (error.response?.status === 401) {
        errorMessage = 'Authentication failed. Please log in again.';
      } else if (error.response?.status === 403) {
        errorMessage = 'You do not have permission to upload images.';
      } else if (error.response?.data?.error) {
        errorMessage = `Upload error: ${error.response.data.error}`;
      } else if (error.message) {
        errorMessage = `Upload error: ${error.message}`;
      }
      
      alert(errorMessage);
    } finally {
      setUploadingImages(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (isEditing) {
        await api.put(`/api/products/${id}`, formData);
      } else {
        await api.post('/api/products', formData);
      }
      navigate('/admin/products');
    } catch (error: any) {
      console.error('Error details:', error.response?.data);
      alert(error.response?.data?.error || 'Error saving product');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
              [name]: name === 'price' ? Number(value) : value
    }));
  };

  const addFeature = () => {
    if (newFeature.trim()) {
      setFormData(prev => ({
        ...prev,
        features: [...prev.features, newFeature.trim()]
      }));
      setNewFeature('');
    }
  };

  const removeFeature = (index: number) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const addSpecification = () => {
    if (newSpecKey.trim() && newSpecValue.trim()) {
      setFormData(prev => ({
        ...prev,
        specifications: {
          ...prev.specifications,
          [newSpecKey.trim()]: newSpecValue.trim()
        }
      }));
      setNewSpecKey('');
      setNewSpecValue('');
    }
  };

  const removeSpecification = (key: string) => {
    setFormData(prev => {
      const newSpecs = { ...prev.specifications };
      delete newSpecs[key];
      return { ...prev, specifications: newSpecs };
    });
  };

  const removeImage = (index: number) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const addColor = () => {
    const normalized = newColor.trim().toUpperCase();
    if (!/^#([0-9A-F]{6})$/.test(normalized)) {
      alert('Please pick a valid color.');
      return;
    }

    setFormData((prev) => {
      if (prev.colors.includes(normalized)) return prev;
      return { ...prev, colors: [...prev.colors, normalized] };
    });
  };

  const removeColor = (color: string) => {
    setFormData((prev) => ({
      ...prev,
      colors: prev.colors.filter((existing) => existing !== color)
    }));
  };

  const isImageUrl = (url: string) => {
    return url.startsWith('http') || url.startsWith('data:');
  };

  const getImageUrl = (imagePath: string) => {
    if (!imagePath || imagePath === 'undefined' || imagePath === 'null') {
      return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2Zz4KPHJlY3Qgd2lkdGg9IjEwMCIgaGVpZ2h0PSIxMDAiIGZpbGw9IiNGM0Y0RjYiLz4KPHBhdGggZD0iTTMwIDMwSDcwVjcwSDMwVjMwWiIgZmlsbD0iI0QxRDVEQiIvPgo8cGF0aCBkPSJNNDAgNDBIMjBWNjBIMjBWNDBaIiBmaWxsPSIjQzdDQ0Q3Ii8+Cjwvc3ZnPgo=';
    }
    if (isImageUrl(imagePath)) {
      return imagePath;
    }
    // Handle both forward and backward slashes in paths
    const normalizedPath = imagePath.replace(/\\/g, '/');
    const filename = normalizedPath.split('/').pop();
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    return `${apiUrl}/api/products/image/${filename}`;
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 bg-white dark:bg-dark-bg min-h-screen transition-colors duration-300">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 dark:bg-dark-hover rounded w-1/4 mb-6"></div>
          <div className="space-y-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-12 bg-gray-200 dark:bg-dark-hover rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 bg-white dark:bg-dark-bg min-h-screen transition-colors duration-300">
      <div className="mb-8">
                  <button
            onClick={() => navigate('/admin/products')}
            className="flex items-center text-gray-600 dark:text-dark-textSecondary hover:text-gray-900 dark:hover:text-dark-text mb-4 transition-colors duration-300"
          >
            <ArrowLeft className="h-4 w-4 ml-2" />
            العودة إلى المنتجات
          </button>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white transition-colors duration-300">
          {isEditing ? 'تعديل المنتج' : 'إضافة منتج جديد'}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="name" className="form-label">اسم المنتج</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              className="input-field"
              placeholder="أدخل اسم المنتج"
            />
          </div>

          <div>
            <label htmlFor="category" className="form-label">الفئة</label>
            <div className="flex space-x-2 space-x-reverse">
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                required
                className="input-field flex-1"
              >
                <option value="">اختر الفئة</option>
                {categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => {
                  const newCategory = prompt('أدخل اسم الفئة الجديدة:');
                  if (newCategory && newCategory.trim() && !categories.includes(newCategory.trim())) {
                    setCategories(prev => [...prev, newCategory.trim()]);
                    setFormData(prev => ({ ...prev, category: newCategory.trim() }));
                  }
                }}
                className="btn-secondary px-3 whitespace-nowrap"
              >
                فئة جديدة
              </button>
            </div>
          </div>

          <div>
                            <label htmlFor="price" className="form-label">السعر (₪)</label>
            <input
              type="number"
              id="price"
              name="price"
              value={formData.price}
              onChange={handleInputChange}
              required
              min="0"
              step="0.01"
              className="input-field"
              placeholder="0.00"
            />
          </div>


        </div>

        <div>
          <label htmlFor="description" className="form-label">الوصف</label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            required
            rows={4}
            className="input-field"
            placeholder="أدخل وصف المنتج"
          />
        </div>

        <div>
          <label className="form-label">اللون (اختياري)</label>
          <div className="rounded-xl border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-surface p-4 mb-3 space-y-3">
            <div>
              <label className="block text-xs text-gray-600 dark:text-dark-textSecondary mb-1">Hue</label>
              <input
                type="range"
                min={0}
                max={360}
                value={hue}
                onChange={(e) => setHue(Number(e.target.value))}
                className="w-full min-h-0 h-2 rounded-lg appearance-none cursor-pointer"
                style={{
                  direction: 'ltr',
                  background:
                    'linear-gradient(90deg, #ff0000 0%, #ffff00 17%, #00ff00 33%, #00ffff 50%, #0000ff 67%, #ff00ff 83%, #ff0000 100%)',
                  accentColor: '#db2777'
                }}
                aria-label="Hue slider"
              />
            </div>

            <div>
              <label className="block text-xs text-gray-600 dark:text-dark-textSecondary mb-1">Saturation</label>
              <input
                type="range"
                min={0}
                max={100}
                value={saturation}
                onChange={(e) => setSaturation(Number(e.target.value))}
                className="w-full min-h-0 h-2 rounded-lg appearance-none cursor-pointer"
                style={{
                  direction: 'ltr',
                  background: `linear-gradient(90deg, ${hsvToHex(hue, 0, 100)} 0%, ${hsvToHex(
                    hue,
                    100,
                    100
                  )} 100%)`,
                  accentColor: '#db2777'
                }}
                aria-label="Saturation slider"
              />
            </div>

            <div>
              <label className="block text-xs text-gray-600 dark:text-dark-textSecondary mb-1">Brightness</label>
              <input
                type="range"
                min={0}
                max={100}
                value={brightness}
                onChange={(e) => setBrightness(Number(e.target.value))}
                className="w-full min-h-0 h-2 rounded-lg appearance-none cursor-pointer"
                style={{
                  direction: 'ltr',
                  background: 'linear-gradient(90deg, #000000 0%, #FFFFFF 100%)',
                  accentColor: '#db2777'
                }}
                aria-label="Brightness slider"
              />
            </div>

            <div className="flex items-center justify-between pt-1">
              <div className="flex items-center gap-2">
                <span
                  className="inline-block h-8 w-8 rounded-md border border-gray-300 dark:border-dark-border"
                  style={{ backgroundColor: newColor }}
                  aria-label="Selected color preview"
                />
                <span className="text-sm font-medium text-gray-700 dark:text-dark-text">{newColor}</span>
              </div>
              <button
                type="button"
                onClick={addColor}
                className="btn-secondary px-4"
              >
                إضافة لون
              </button>
            </div>
          </div>
          {formData.colors.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {formData.colors.map((color) => (
                <div key={color} className="flex items-center gap-2 rounded-full border border-gray-200 dark:border-dark-border px-3 py-1 bg-white dark:bg-dark-surface">
                  <span className="inline-block h-5 w-5 rounded-full border border-gray-300 dark:border-dark-border" style={{ backgroundColor: color }} />
                  <span className="text-sm text-gray-700 dark:text-dark-text">{color}</span>
                  <button
                    type="button"
                    onClick={() => removeColor(color)}
                    className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                    aria-label={`Remove color ${color}`}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <label className="form-label">صور المنتج</label>
          
          {/* File Upload Section */}
          <div className="mb-4">
            <div 
              className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-primary-400 transition-colors"
              onDragOver={(e) => {
                e.preventDefault();
                e.currentTarget.classList.add('border-primary-400', 'bg-primary-50');
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                e.currentTarget.classList.remove('border-primary-400', 'bg-primary-50');
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.currentTarget.classList.remove('border-primary-400', 'bg-primary-50');
                const files = e.dataTransfer.files;
                if (files.length > 0) {
                  handleFileUpload(files);
                }
              }}
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*"
                onChange={(e) => e.target.files && handleFileUpload(e.target.files)}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingImages}
                className="flex flex-col items-center space-y-2 text-gray-600 hover:text-primary-600 disabled:opacity-50"
              >
                <Upload className={`h-8 w-8 ${uploadingImages ? 'animate-bounce' : ''}`} />
                <span className="text-sm">
                  {uploadingImages ? 'جاري رفع الصور...' : 'اضغط لرفع الصور أو اسحبها هنا'}
                </span>
                <span className="text-xs text-gray-500">JPG, PNG, GIF حتى 5MB</span>
                {uploadingImages && (
                  <div className="mt-2 text-xs text-primary-600">يرجى الانتظار...</div>
                )}
              </button>
            </div>
          </div>

          {/* Image Preview Section - Show uploaded images first */}
          {formData.images.length > 0 && (
            <div className="mb-4">
              <label className="text-sm font-medium text-gray-700 mb-2 block">الصور المرفوعة:</label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {formData.images.map((image, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={getImageUrl(image)}
                      alt={`صورة ${index + 1}`}
                      className="w-full h-24 object-cover rounded-lg border border-gray-200"
                      onError={(e) => {
                        e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0zMCAzMEg3MFY3MEgzMFYzMFoiIGZpbGw9IiNEMUQ1REIiLz4KPHBhdGggZD0iTTQwIDQwSDYwVjYwSDQwVjQwWiIgZmlsbD0iI0M3Q0RDNyIvPgo8L3N2Zz4K';
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => removeImage(index)}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Image URLs Section - Only for external URLs */}
          <div className="mb-4">
            <label className="text-sm font-medium text-gray-700 mb-2 block">أو أضف روابط الصور الخارجية:</label>
            <div className="space-y-2">
              {formData.images.filter(img => img.startsWith('http')).map((image, index) => (
                <div key={index} className="flex items-center space-x-2 space-x-reverse">
                  <span className="text-sm text-gray-600 flex-1">{image}</span>
                  <button
                    type="button"
                    onClick={() => removeImage(index)}
                    className="btn-danger px-3"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => {
                  const url = prompt('أدخل رابط الصورة الخارجية (يجب أن يبدأ بـ http:// أو https://):');
                  if (url && url.trim()) {
                    if (url.startsWith('http://') || url.startsWith('https://')) {
                      setFormData(prev => ({ ...prev, images: [...prev.images, url.trim()] }));
                    } else {
                      alert('يجب أن يبدأ الرابط بـ http:// أو https://');
                    }
                  }
                }}
                className="btn-secondary flex items-center space-x-2 space-x-reverse"
              >
                <Plus className="h-4 w-4" />
                <span>إضافة رابط صورة خارجية</span>
              </button>
            </div>
          </div>
        </div>

        <div>
          <label className="form-label">المميزات</label>
          <div className="space-y-2">
            {formData.features.map((feature, index) => (
              <div key={index} className="flex items-center space-x-2">
                <span className="text-gray-600">•</span>
                <span className="flex-1">{feature}</span>
                <button
                  type="button"
                  onClick={() => removeFeature(index)}
                  className="text-red-600 hover:text-red-800"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
            <div className="flex space-x-2">
              <input
                type="text"
                value={newFeature}
                onChange={(e) => setNewFeature(e.target.value)}
                className="input-field flex-1"
                                 placeholder="أدخل الميزة"
              />
              <button
                type="button"
                onClick={addFeature}
                className="btn-secondary px-4"
              >
                إضافة
              </button>
            </div>
          </div>
        </div>

        <div>
          <label className="form-label">المواصفات</label>
          <div className="space-y-2">
            {Object.entries(formData.specifications).map(([key, value]) => (
              <div key={key} className="flex items-center space-x-2">
                <span className="text-gray-600 font-medium">{key}:</span>
                <span className="flex-1">{value}</span>
                <button
                  type="button"
                  onClick={() => removeSpecification(key)}
                  className="text-red-600 hover:text-red-800"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
            <div className="grid grid-cols-2 gap-2">
              <input
                type="text"
                value={newSpecKey}
                onChange={(e) => setNewSpecKey(e.target.value)}
                className="input-field"
                placeholder="اسم المواصفة"
              />
              <input
                type="text"
                value={newSpecValue}
                onChange={(e) => setNewSpecValue(e.target.value)}
                className="input-field"
                placeholder="قيمة المواصفة"
              />
            </div>
            <button
              type="button"
              onClick={addSpecification}
              className="btn-secondary px-4"
            >
              إضافة مواصفة
            </button>
          </div>
        </div>

        <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={() => navigate('/admin/products')}
            className="btn-secondary"
          >
            إلغاء
          </button>
          <button
            type="submit"
            disabled={saving}
            className="btn-primary flex items-center space-x-2 disabled:opacity-50"
          >
            <Save className="h-4 w-4" />
            <span>{saving ? 'جاري الحفظ...' : (isEditing ? 'تحديث المنتج' : 'إنشاء المنتج')}</span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminProductForm;
